/**
 * Utilitaire de compression d'image via Canvas API.
 * Réduit les photos terrain (~3-5 Mo) à ~200-400 Ko pour le stockage offline.
 */

interface CompressOptions {
  /** Largeur max en pixels (défaut : 1200) */
  maxWidth?: number;
  /** Qualité JPEG 0-1 (défaut : 0.75) */
  quality?: number;
}

/**
 * Compresse un Blob image en JPEG redimensionné.
 * Utilise le Canvas API natif — pas de dépendance externe.
 */
export async function compressImage(
  blob: Blob,
  options?: CompressOptions
): Promise<Blob> {
  const { maxWidth = 1200, quality = 0.75 } = options ?? {};

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculer les dimensions de sortie
      let outWidth = img.naturalWidth;
      let outHeight = img.naturalHeight;

      if (outWidth > maxWidth) {
        outHeight = Math.round((outHeight * maxWidth) / outWidth);
        outWidth = maxWidth;
      }

      // Dessiner sur canvas et exporter en JPEG
      const canvas = document.createElement("canvas");
      canvas.width = outWidth;
      canvas.height = outHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Impossible de créer le contexte canvas"));
        return;
      }

      ctx.drawImage(img, 0, 0, outWidth, outHeight);

      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            // Fallback : retourner le blob original
            resolve(blob);
          }
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Impossible de charger l'image"));
    };

    img.src = url;
  });
}
